import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, fileUrl } from "../api";
import { BrutalButton, BrutalCard, BrutalInput, BrutalTextarea } from "../components/Brutal";
import { Camera, Upload } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";

export default function AddProductPage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { canSeeCost } = useAuth();
  const [form, setForm] = useState({
    name: "",
    description: "",
    starting_stock: "",
    current_stock: "",
    unit: "units",
    photo_path: null,
    wholesale_price: "",
    suggested_retail_price: "",
    landed_cost: "",
    category: "Injectable",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [multiDetected, setMultiDetected] = useState(false);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/scan-one", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Auto-fill any detected fields without clobbering manual edits
      setForm((f) => ({
        ...f,
        photo_path: data.photo_path || f.photo_path,
        name: f.name || data.name || "",
        unit: data.unit && f.unit === "units" ? data.unit : f.unit,
        description: f.description || [
          data.sku ? `SKU: ${data.sku}` : "",
          data.category ? `Category: ${data.category}` : "",
        ].filter(Boolean).join(" · "),
      }));
      if (data.warning) {
        toast.message(data.warning);
      } else if (data.multiple_products) {
        setMultiDetected(true);
        toast.warning(t("add_product.multi_detected"));
      } else if (data.detected) {
        setMultiDetected(false);
        toast.success(t("add_product.ocr_detected"));
      } else {
        toast.message(t("add_product.ocr_no_detect"));
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const starting = Number(form.starting_stock);
      const current = form.current_stock === "" ? starting : Number(form.current_stock);
      await api.post("/products", {
        name: form.name.trim(),
        description: form.description.trim(),
        starting_stock: starting,
        current_stock: current,
        unit: form.unit.trim() || "units",
        photo_path: form.photo_path,
        wholesale_price: form.wholesale_price === "" ? null : Number(form.wholesale_price),
        suggested_retail_price: form.suggested_retail_price === "" ? null : Number(form.suggested_retail_price),
        landed_cost: canSeeCost && form.landed_cost !== "" ? Number(form.landed_cost) : null,
        category: form.category,
      });
      toast.success(t("add_product.added"));
      nav("/inventory");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="add-product-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">{t("add_product.title")}</h1>
      <p className="text-sm sm:text-base text-zinc-600 mb-6">
        {t("add_product.subtitle")}
      </p>

      <BrutalCard>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-2">{t("add_product.photo")}</label>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50 flex items-center justify-center shrink-0">
                {form.photo_path ? (
                  <img
                    src={fileUrl(form.photo_path)}
                    alt=""
                    className="w-full h-full object-cover"
                    crossOrigin="use-credentials"
                  />
                ) : (
                  <Camera size={32} weight="bold" />
                )}
              </div>
              <label
                className="btn-brutal bg-white text-black h-14 px-5 cursor-pointer"
                data-testid="upload-photo-label"
              >
                {uploading ? (
                  <span className="inline-block w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" data-testid="ocr-spinner" />
                ) : (
                  <Upload size={18} weight="bold" />
                )}
                {uploading ? t("add_product.ocr_running") : form.photo_path ? t("add_product.change_photo") : t("add_product.upload_photo")}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhoto}
                  data-testid="photo-input"
                  disabled={uploading}
                />
              </label>
            </div>
            <p className="text-xs text-zinc-500 mt-2 ml-32 -mt-3">{t("add_product.ocr_hint")}</p>
            <p className="text-[11px] text-zinc-400 ml-32">{t("add_product.photo_tips")}</p>
            {multiDetected && (
              <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900" data-testid="multi-product-warning">
                {t("add_product.multi_detected")}{" "}
                <Link to="/inventory/intake" className="font-semibold underline" data-testid="multi-product-intake-link">
                  {t("add_product.multi_detected_cta")}
                </Link>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold mb-2">{t("add_product.name_label")}</label>
            <BrutalInput
              data-testid="product-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={120}
              placeholder={t("add_product.name_placeholder")}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-2">{t("add_product.desc_label")}</label>
            <BrutalTextarea
              rows={2}
              data-testid="product-description-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t("add_product.desc_placeholder")}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-2">{t("add_product.starting_stock")}</label>
              <BrutalInput
                data-testid="starting-stock-input"
                type="number"
                min="0"
                value={form.starting_stock}
                onChange={(e) => setForm({ ...form, starting_stock: e.target.value })}
                required
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-2">{t("add_product.current_stock")}</label>
              <BrutalInput
                data-testid="current-stock-input"
                type="number"
                min="0"
                value={form.current_stock}
                onChange={(e) => setForm({ ...form, current_stock: e.target.value })}
                placeholder={t("add_product.current_placeholder")}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-2">{t("add_product.unit_label")}</label>
            <BrutalInput
              data-testid="unit-input"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              placeholder={t("add_product.unit_placeholder")}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-2">{t("categories.label")}</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="add-category-select"
              className="w-full h-11 px-3 bg-white border border-zinc-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#c9a24a]"
            >
              {["Injectable", "Precision Nasal Mist", "Topical", "Flagship Topical", "supplies",
                "Mushroom Coffee", "Adaptogens", "Protein", "Supplements"].map((c) => (
                <option key={c} value={c}>{t(`categories.${c}`)}</option>
              ))}
            </select>
          </div>

          {canSeeCost && (
            <div>
              <label className="block text-xs font-semibold mb-2 text-[#8a6a1c]">{t("pricing.landed_cost")}</label>
              <BrutalInput
                data-testid="add-landed-cost-input"
                type="number"
                step="0.01"
                min="0"
                value={form.landed_cost}
                onChange={(e) => setForm({ ...form, landed_cost: e.target.value })}
                placeholder="0.00"
                className="!border-[#c9a24a]"
              />
              <p className="text-[11px] text-zinc-500 mt-1">{t("pricing.landed_cost_hint")}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-2">
                {t("pricing.wholesale_price")} <span className="text-zinc-400 font-normal">({t("common.optional")})</span>
              </label>
              <BrutalInput
                data-testid="wholesale-price-input"
                type="number"
                step="0.01"
                min="0"
                value={form.wholesale_price}
                onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-2">
                {t("pricing.suggested_retail")} <span className="text-zinc-400 font-normal">({t("common.optional")})</span>
              </label>
              <BrutalInput
                data-testid="retail-price-input"
                type="number"
                step="0.01"
                min="0"
                value={form.suggested_retail_price}
                onChange={(e) => setForm({ ...form, suggested_retail_price: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <BrutalButton
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={() => nav(-1)}
            >
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              type="submit"
              className="flex-1"
              disabled={saving || uploading}
              data-testid="save-product-button"
            >
              {saving ? t("common.saving") : t("add_product.save_product")}
            </BrutalButton>
          </div>
        </form>
      </BrutalCard>
    </div>
  );
}
